import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Cropper from 'react-easy-crop';
import {
  XMarkIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ArrowPathIcon,
  ScissorsIcon,
  EyeIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

interface ReceiptPreviewProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  onSaveCropped?: (croppedBlob: Blob) => void;
}

const ReceiptPreview: React.FC<ReceiptPreviewProps> = ({
  isOpen,
  onClose,
  imageUrl,
  onSaveCropped,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [isCropping, setIsCropping] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const onCropComplete = useCallback((croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setIsCropping(false);
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.25));
  };

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', error => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: any,
    rotation = 0
  ): Promise<Blob | null> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return null;
    }

    const maxSize = Math.max(image.naturalWidth, image.naturalHeight);
    const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

    // set each dimensions to double largest dimension to allow for a safe area for the
    // image to rotate in without being clipped by canvas context
    canvas.width = safeArea;
    canvas.height = safeArea;

    // translate canvas context to a central location on image to allow rotating around the center.
    ctx.translate(safeArea / 2, safeArea / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-safeArea / 2, -safeArea / 2);

    // draw rotated image and store data.
    ctx.drawImage(
      image,
      safeArea / 2 - image.naturalWidth / 2,
      safeArea / 2 - image.naturalHeight / 2
    );

    const data = ctx.getImageData(0, 0, safeArea, safeArea);

    // set canvas width to final desired crop size - this will clear existing context
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // paste generated rotated image with correct offsets for x,y crop values.
    ctx.putImageData(
      data,
      Math.round(0 - safeArea / 2 + image.naturalWidth / 2 - pixelCrop.x),
      Math.round(0 - safeArea / 2 + image.naturalHeight / 2 - pixelCrop.y)
    );

    return new Promise(resolve => {
      canvas.toBlob(
        blob => {
          resolve(blob);
        },
        'image/jpeg',
        0.95
      );
    });
  };

  const handleSaveCropped = async () => {
    if (!croppedAreaPixels || !onSaveCropped) return;

    setIsProcessing(true);
    try {
      const croppedBlob = await getCroppedImg(imageUrl, croppedAreaPixels, rotation);
      if (croppedBlob) {
        onSaveCropped(croppedBlob);
        onClose();
      }
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOpenOriginal = () => {
    window.open(imageUrl, '_blank');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative max-w-4xl w-full mx-4 bg-white rounded-lg shadow-xl overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Receipt Preview</h3>
            <button
              onClick={onClose}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <XMarkIcon className="h-6 w-6 text-gray-400" />
            </button>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between p-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <button
                onClick={handleZoomOut}
                className="p-2 rounded-md hover:bg-white hover:shadow-sm transition-all"
                aria-label="Zoom out"
              >
                <MagnifyingGlassMinusIcon className="h-5 w-5 text-gray-600" />
              </button>

              <span className="text-sm text-gray-600 min-w-[60px] text-center">
                {Math.round(zoom * 100)}%
              </span>

              <button
                onClick={handleZoomIn}
                className="p-2 rounded-md hover:bg-white hover:shadow-sm transition-all"
                aria-label="Zoom in"
              >
                <MagnifyingGlassPlusIcon className="h-5 w-5 text-gray-600" />
              </button>

              <button
                onClick={handleRotate}
                className="p-2 rounded-md hover:bg-white hover:shadow-sm transition-all ml-2"
                aria-label="Rotate 90°"
              >
                <ArrowPathIcon className="h-5 w-5 text-gray-600" />
              </button>

              <button
                onClick={handleReset}
                className="p-2 rounded-md hover:bg-white hover:shadow-sm transition-all ml-2"
                aria-label="Reset view"
              >
                <EyeIcon className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsCropping(!isCropping)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  isCropping
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                <ScissorsIcon className="h-4 w-4 inline mr-1" />
                {isCropping ? 'Cropping' : 'Crop'}
              </button>

              <button
                onClick={handleOpenOriginal}
                className="px-3 py-1.5 rounded-md text-sm font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 transition-all"
              >
                <ArrowDownTrayIcon className="h-4 w-4 inline mr-1" />
                Original
              </button>

              {isCropping && onSaveCropped && (
                <button
                  onClick={handleSaveCropped}
                  disabled={isProcessing}
                  className="px-3 py-1.5 rounded-md text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-all"
                >
                  {isProcessing ? 'Processing...' : 'Save Cropped'}
                </button>
              )}
            </div>
          </div>

          {/* Image Display */}
          <div className="relative bg-gray-900" style={{ height: '70vh', maxHeight: '600px' }}>
            {isCropping ? (
              <Cropper
                image={imageUrl}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={undefined}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                cropShape="rect"
                showGrid={true}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center p-4">
                <img
                  src={imageUrl}
                  alt="Receipt"
                  className="max-w-full max-h-full object-contain"
                  style={{
                    transform: `rotate(${rotation}deg) scale(${zoom})`,
                    transformOrigin: 'center',
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ReceiptPreview;
